/**
 * Pure view model for the web Joining card.
 *
 * This module is pure: no React import, no DOM, no fetch, no router state
 * (`plan.md ## 5.29`). The three toggle `checked` expressions come straight
 * from `plan.md ## 5.17`, the single authority for all three values so the
 * web and mobile cards cannot disagree.
 */
import {
  JOINING_APPROVE_MYSELF_DESCRIPTION,
  JOINING_APPROVE_MYSELF_LABEL,
  JOINING_MEMBER_SHARING_DESCRIPTION,
  JOINING_MEMBER_SHARING_LABEL,
  JOINING_POOL_ID_PENDING_VALUE,
  JOINING_PUBLIC_LISTING_DESCRIPTION,
  JOINING_PUBLIC_LISTING_LABEL,
} from "./joiningCardCopy";

export type typeJoiningToggleField =
  "approveMyself" | "memberSharing" | "publicListing";

export type typeJoiningToggleRow = {
  field: typeJoiningToggleField;
  label: string;
  description: string;
  checked: boolean;
};

/** Only the four `/pool/get` fields this card reads. See `plan.md ## 5.22`. */
export type typeJoiningPoolInput = {
  autoApprove?: boolean | undefined;
  memberSharingEnabled?: boolean | undefined;
  visibility?: "public" | "private" | undefined;
  poolRefId?: string | null | undefined;
  /** Names the pool on the invite QR code's scan view (ISSUE-1339). */
  name?: string | undefined;
};

export type typeJoiningUpdateBody =
  | { autoApprove: boolean }
  | { memberSharingEnabled: boolean }
  | { visibility: "public" | "private" };

export type typeInviteCodeState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "ready"; joinCode: string };

export function resolveJoiningToggles(
  pool: typeJoiningPoolInput,
): readonly typeJoiningToggleRow[] {
  return [
    {
      field: "approveMyself",
      label: JOINING_APPROVE_MYSELF_LABEL,
      description: JOINING_APPROVE_MYSELF_DESCRIPTION,
      checked: !(pool.autoApprove ?? false),
    },
    {
      field: "memberSharing",
      label: JOINING_MEMBER_SHARING_LABEL,
      description: JOINING_MEMBER_SHARING_DESCRIPTION,
      checked: pool.memberSharingEnabled ?? true,
    },
    {
      field: "publicListing",
      label: JOINING_PUBLIC_LISTING_LABEL,
      description: JOINING_PUBLIC_LISTING_DESCRIPTION,
      checked: (pool.visibility ?? "private") === "public",
    },
  ];
}

export function buildJoiningUpdateBody(
  field: typeJoiningToggleField,
  nextChecked: boolean,
): typeJoiningUpdateBody {
  if (field === "approveMyself") return { autoApprove: !nextChecked };
  if (field === "memberSharing") return { memberSharingEnabled: nextChecked };
  return { visibility: nextChecked ? "public" : "private" };
}

export function resolvePoolIdValue(
  poolRefId: string | null | undefined,
): string {
  if (typeof poolRefId === "string" && poolRefId.length > 0) return poolRefId;
  return JOINING_POOL_ID_PENDING_VALUE;
}

export function resolveInviteCodeState(input: {
  isLoading: boolean;
  isTransportError: boolean;
  errorCode: string | null;
  joinCode: string | null;
}): typeInviteCodeState {
  if (input.isTransportError) return { phase: "failed" };
  if (input.errorCode !== null) return { phase: "failed" };
  if (input.isLoading) return { phase: "loading" };
  if (input.joinCode === null || input.joinCode === "") {
    return { phase: "failed" };
  }
  return { phase: "ready", joinCode: input.joinCode };
}
